import {useCallback, useEffect, useRef, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {merchantService} from '@/services/merchants';
import type {AfriexQueryParams} from '@/types';
import type {AfriexMerchant} from '@/types/AdminBusinessUser';
import {logRejection} from '@/utils/logRejection';

const useMerchantUserList = () => {
  const [merchantList, setMerchantList] = useState<AfriexMerchant[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch] = useState<boolean>(false);
  const [userTotal, setUserTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  //sequence token: a request only commits its result if no newer one has
  //started, so a slow response cannot overwrite a fresher one
  const latestRequestRef = useRef(0);

  const fetchAllMerchants = useCallback(
    async (params?: AfriexQueryParams) => {
      const seq = ++latestRequestRef.current;
      try {
        setError(null);
        setIsLoading(true);
        const {data, total} = await merchantService.getBusinessUsers({
          ...params,
          ...currentFilters,
        });
        if (seq !== latestRequestRef.current) {
          return;
        }
        setMerchantList(data);
        setUserTotal(total || 0);
      } catch (e) {
        if (seq !== latestRequestRef.current) {
          return;
        }
        setError(e);
      } finally {
        //only the newest request clears the spinner, a late older one must not
        if (seq === latestRequestRef.current) {
          setIsLoading(false);
        }
      }
    },
    [currentFilters],
  );

  const handleUserAutoComplete = useCallback(
    async (searchTerm: string) => {
      const seq = ++latestRequestRef.current;
      try {
        setError(null);
        setIsLoading(true);
        setShowPagination(false);

        if (!searchTerm?.trim() || searchTerm.length < 3) {
          //empty/short term restores the full list; fetchAllMerchants owns
          //loading from here
          await fetchAllMerchants({});
          return;
        }
        const users = await merchantService.searchUserAutoComplete(
          searchTerm?.toLowerCase(),
        );
        if (seq !== latestRequestRef.current) {
          //a newer request already landed (Clear restored the list), don't put
          //the searched user back on screen
          return;
        }
        if (users) {
          setMerchantList(users);
          setUserTotal(1);
        }
      } catch (e) {
        if (seq !== latestRequestRef.current) {
          return;
        }
        setError(e);
      }
      if (seq === latestRequestRef.current) {
        setIsLoading(false);
      }
    },
    [fetchAllMerchants],
  );

  useEffect(() => {
    // `fetchAllMerchants` catches into `error` state, so this only fires if
    // that catch or the finally itself throws. Log rather than swallow.
    fetchAllMerchants({}).catch(
      logRejection('Failed to load merchant user list'),
    );
  }, [fetchAllMerchants]);

  // Modified handleApplyFilters to decide which API to call
  const handleApplyFilters = useCallback(() => {
    applyFilters(filtersToApply);
    setIsPageReset((prev) => !prev); // Reset pagination to the first page
  }, [applyFilters, filtersToApply]);

  // Handle change for instant data fetching
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    //only mint a new currentFilters when there is something to clear, a fresh
    //{} reads as a filter change and refetches the whole list
    setCurrentFilters((prev) => (Object.keys(prev).length ? {} : prev));
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  return {
    error,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: userTotal,
    },
    showPagination,
    filterCount,
    isPageReset,
    currentFilters, // automatically triggers fetch
    filtersToApply, // requires handleApplyFilters to trigger fetch
    clearFilters: cleanFilters,
    selectAppliedFilters, // replaces handleDateChange
    handleApplyFilters,
    selectCurrentFilters, // replaces handleFilterChange
    handleUserAutoComplete,
    fetchAllMerchants,
    merchantList,
  };
};

export type MerchantUserListProps = ReturnType<typeof useMerchantUserList>;

export default useMerchantUserList;
